// src/components/UserAdminModal.jsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * UserAdminModal (AUTH + RBAC)
 * - AUTH roles are fixed to: admin / staff (because auth_routes.py only allows those)
 * - RBAC roles are dynamic and managed via /rbac/*
 *
 * Requires apiFetch(path, {method, body}) that returns parsed JSON or throws Error.
 */

export default function UserAdminModal({ open, onClose, apiFetch }) {
  const BRANCH_OPTIONS = useMemo(
    () => [
      { value: "jounieh", label: "Jounieh" },
      { value: "tripoli", label: "Tripoli" },
      { value: "batroun", label: "Batroun" },
      { value: "zgharta", label: "Zgharta" },
      { value: "ehden", label: "Ehden" },
    ],
    []
  );

  // ✅ AUTH roles only (DO NOT add new ones unless you change auth_routes.py)
  const AUTH_ROLE_OPTIONS = useMemo(
    () => [
      { value: "staff", label: "Staff" },
      { value: "admin", label: "Admin" },
    ],
    []
  );

  const [tab, setTab] = useState("users"); // users | create
  const [busy, setBusy] = useState(false);

  // -------------------------
  // Users list state
  // -------------------------
  const [users, setUsers] = useState([]);
  const [loadErr, setLoadErr] = useState("");
  const [q, setQ] = useState("");
  const [filterRole, setFilterRole] = useState(""); // RBAC role filter
  const [filterBranch, setFilterBranch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all | active | inactive

  // RBAC catalog
  const [rbacRoles, setRbacRoles] = useState([]); // [{name, description, perms}]
  const [rbacUsersMap, setRbacUsersMap] = useState({}); // email -> [roles]

  // Edit drawer state
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  // -------------------------
  // Create user state
  // -------------------------
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");

  // Auth roles (admin/staff only) - for auth_routes.py
  const [newAuthRoles, setNewAuthRoles] = useState("staff"); // staff/admin/admin,staff

  // RBAC roles (dynamic) - for /rbac/*
  const [newRbacRoles, setNewRbacRoles] = useState([]);

  const [newAllBranches, setNewAllBranches] = useState(false);
  const [newBranchesSelected, setNewBranchesSelected] = useState([]);

  const resetNewUserForm = () => {
    setNewEmail("");
    setNewPassword("");
    setNewFirstName("");
    setNewLastName("");
    setNewAuthRoles("staff");
    setNewRbacRoles([]);
    setNewAllBranches(false);
    setNewBranchesSelected([]);
  };

  const toggleBranch = (val, setter) => {
    setter((prev) => {
      const set = new Set(prev || []);
      if (set.has(val)) set.delete(val);
      else set.add(val);
      return Array.from(set);
    });
  };

  const normalizeCsvRoles = (rolesStrOrArr) => {
    if (Array.isArray(rolesStrOrArr)) {
      return rolesStrOrArr.map((s) => String(s).trim().toLowerCase()).filter(Boolean);
    }
    return String(rolesStrOrArr || "")
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  };

  const rolesToStr = (rolesArr) => (rolesArr || []).join(",");

  // -------------------------
  // Load RBAC catalog + user assignments
  // -------------------------
  const refreshRbac = async () => {
    // RBAC roles catalog
    let rolesList = [];
    try {
      const rr = await apiFetch("/rbac/roles", { method: "GET" });
      rolesList = Array.isArray(rr?.roles) ? rr.roles : [];
    } catch {
      rolesList = [];
    }
    setRbacRoles(rolesList);

    // RBAC user assignments
    let map = {};
    try {
      const uu = await apiFetch("/rbac/users", { method: "GET" });
      const list = Array.isArray(uu?.users) ? uu.users : [];
      for (const it of list) {
        const email = String(it?.email || "").toLowerCase().trim();
        if (!email) continue;
        const roles = Array.isArray(it?.roles) ? it.roles : [];
        map[email] = roles.map((x) => String(x).toLowerCase().trim()).filter(Boolean);
      }
    } catch {
      map = {};
    }
    setRbacUsersMap(map);
  };

  // -------------------------
  // Users list (AUTH) + merge RBAC roles
  // -------------------------
  const refreshUsers = async () => {
    setBusy(true);
    setLoadErr("");
    try {
      // load both in parallel
      const [authRes] = await Promise.all([
        apiFetch("/auth/users", { method: "GET" }),
        refreshRbac(),
      ]);

      const list = Array.isArray(authRes?.users)
        ? authRes.users
        : Array.isArray(authRes)
        ? authRes
        : [];

      // merge RBAC roles onto each user
      const merged = (list || []).map((u) => {
        const email = String(u?.email || "").toLowerCase().trim();
        const rbac = rbacUsersMap[email] || [];
        return { ...u, rbac_roles: rbac };
      });

      setUsers(merged);
    } catch (e) {
      setLoadErr(e?.message || String(e));
      setUsers([]);
    } finally {
      setBusy(false);
    }
  };

  // When RBAC map changes, re-merge into users list
  useEffect(() => {
    if (!open) return;
    setUsers((prev) =>
      (prev || []).map((u) => {
        const email = String(u?.email || "").toLowerCase().trim();
        return { ...u, rbac_roles: rbacUsersMap[email] || [] };
      })
    );
  }, [rbacUsersMap, open]);

  // reset + load each open
  useEffect(() => {
    if (!open) return;
    setTab("users");
    setEditing(null);
    setEditDraft(null);
    resetNewUserForm();

    // Ensure RBAC catalog is loaded before filtering/role picker
    (async () => {
      await refreshRbac();
      await refreshUsers();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ESC close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !creating && !busy) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, creating, busy, onClose]);

  // lock scroll when open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  // RBAC filter options
  const RBAC_ROLE_OPTIONS = useMemo(() => {
    const list = (rbacRoles || []).map((r) => ({
      value: String(r?.name || "").toLowerCase(),
      label: String(r?.name || "").toLowerCase(),
      description: String(r?.description || ""),
    }));
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [rbacRoles]);

  const filteredUsers = useMemo(() => {
    const qq = (q || "").trim().toLowerCase();
    return (users || [])
      .filter((u) => {
        if (!u) return false;
        const email = String(u.email || "").toLowerCase();
        const fn = String(u.first_name || "").toLowerCase();
        const ln = String(u.last_name || "").toLowerCase();
        const name = `${fn} ${ln}`.trim();

        const authRoles = (u.roles || []).join(",").toLowerCase();
        const branches = (u.branches || []).join(",").toLowerCase();
        const rbacRoles = (u.rbac_roles || []).join(",").toLowerCase();

        if (qq) {
          const hit =
            email.includes(qq) ||
            name.includes(qq) ||
            authRoles.includes(qq) ||
            rbacRoles.includes(qq) ||
            branches.includes(qq);
          if (!hit) return false;
        }

        // RBAC role filter
        if (filterRole) {
          const rr = (u.rbac_roles || []).map((x) => String(x).toLowerCase());
          if (!rr.includes(filterRole)) return false;
        }

        if (filterBranch) {
          const bb = (u.branches || []).map((x) => String(x).toLowerCase());
          if (!(bb.includes("*") || bb.includes(filterBranch))) return false;
        }

        if (filterStatus === "active" && u.active === false) return false;
        if (filterStatus === "inactive" && u.active !== false) return false;

        return true;
      })
      .sort((a, b) => String(a.email || "").localeCompare(String(b.email || "")));
  }, [users, q, filterRole, filterBranch, filterStatus]);

  // -------------------------
  // RBAC assign helper
  // -------------------------
  const saveUserRbacRoles = async (email, roles) => {
    const e = String(email || "").trim().toLowerCase();
    const rr = (roles || []).map((x) => String(x).trim().toLowerCase()).filter(Boolean);

    // validate roles exist
    const allowed = new Set((rbacRoles || []).map((r) => String(r?.name || "").toLowerCase()));
    for (const r of rr) {
      if (!allowed.has(r)) throw new Error(`Unknown RBAC role: ${r}`);
    }

    await apiFetch(`/rbac/users/${encodeURIComponent(e)}/roles`, {
      method: "PUT",
      body: JSON.stringify({ roles: rr }),
    });

    // refresh local map
    setRbacUsersMap((prev) => ({ ...(prev || {}), [e]: rr }));
  };

  // -------------------------
  // Create user
  // -------------------------
  const handleCreateUser = async () => {
    const email = (newEmail || "").trim().toLowerCase();
    const pw = (newPassword || "").trim();
    const first_name = (newFirstName || "").trim();
    const last_name = (newLastName || "").trim();

    // AUTH roles (admin/staff only)
    const roles = normalizeCsvRoles(newAuthRoles);
    const branches = newAllBranches ? ["*"] : newBranchesSelected || [];

    if (!email || !pw) return alert("Please enter email + password.");
    if (!first_name || !last_name) return alert("Please enter First Name + Last Name.");
    if (!roles.length) return alert("Please select at least one AUTH role (staff/admin).");
    if (!branches.length) return alert("Please select at least one branch (or choose All branches).");

    // RBAC roles can be empty, but recommended to choose at least one
    const rbac = (newRbacRoles || []).map((x) => String(x).toLowerCase().trim()).filter(Boolean);

    setCreating(true);
    try {
      // 1) Create auth user (unchanged endpoint)
      const data = await apiFetch("/auth/new-user", {
        method: "POST",
        body: JSON.stringify({ email, password: pw, first_name, last_name, roles, branches }),
      });

      // 2) Assign RBAC roles (optional)
      if (rbac.length) {
        await saveUserRbacRoles(email, rbac);
      }

      alert(data?.message || "✅ User created");
      resetNewUserForm();
      setTab("users");
      await refreshUsers();
    } catch (err) {
      alert("❌ " + (err?.message || String(err)));
    } finally {
      setCreating(false);
    }
  };

  // -------------------------
  // Edit user
  // -------------------------
  const openEdit = (u) => {
    const email = String(u?.email || "").toLowerCase().trim();
    setEditing(u);
    setEditDraft({
      email,
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      auth_roles: Array.isArray(u.roles) ? u.roles : [], // admin/staff
      branches: Array.isArray(u.branches) ? u.branches : [],
      active: u.active !== false,

      // RBAC roles (dynamic)
      rbac_roles: Array.isArray(u.rbac_roles) ? u.rbac_roles : (rbacUsersMap[email] || []),
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setEditDraft(null);
  };

  const saveEdit = async () => {
    if (!editDraft?.email) return;

    // AUTH patch payload MUST include roles per your auth_routes AdminUserPatchIn
    const payload = {
      first_name: (editDraft.first_name || "").trim(),
      last_name: (editDraft.last_name || "").trim(),
      roles: normalizeCsvRoles(editDraft.auth_roles), // ✅ only admin/staff
      branches: Array.isArray(editDraft.branches) ? editDraft.branches : [],
      active: !!editDraft.active,
    };

    if (!payload.first_name || !payload.last_name) return alert("First name + last name are required.");
    if (!payload.roles.length) return alert("At least one AUTH role is required (staff/admin).");
    if (!payload.branches.length) return alert("At least one branch (or '*') is required.");

    const rbac = (editDraft.rbac_roles || []).map((x) => String(x).trim().toLowerCase()).filter(Boolean);

    setBusy(true);
    try {
      // 1) Update auth user profile (name/branches/active/auth roles)
      const data = await apiFetch(`/auth/users/${encodeURIComponent(editDraft.email)}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      // 2) Update RBAC roles (dynamic)
      await saveUserRbacRoles(editDraft.email, rbac);

      alert(data?.message || "✅ User updated");
      closeEdit();
      await refreshUsers();
    } catch (e) {
      alert("❌ " + (e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    const next = !(u.active !== false);
    const ok = window.confirm(`${next ? "Activate" : "Deactivate"} user ${u.email}?`);
    if (!ok) return;

    setBusy(true);
    try {
      const data = await apiFetch(`/auth/users/${encodeURIComponent(u.email)}/active`, {
        method: "POST",
        body: JSON.stringify({ active: next }),
      });
      alert(data?.message || "✅ Updated");
      await refreshUsers();
    } catch (e) {
      alert("❌ " + (e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async (u) => {
    const ok = window.confirm(`Delete user ${u.email}?\n\nThis cannot be undone.`);
    if (!ok) return;

    setBusy(true);
    try {
      const data = await apiFetch(`/auth/users/${encodeURIComponent(u.email)}`, {
        method: "DELETE",
      });
      alert(data?.message || "✅ Deleted");

      // Also remove RBAC mapping (optional): if you implement DELETE /rbac/users/{email}
      // We'll simply refresh RBAC map after deletion.
      await refreshRbac();
      await refreshUsers();
    } catch (e) {
      alert("❌ " + (e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  };

  const toggleRbacRole = (roleName, setter) => {
    const r = String(roleName || "").trim().toLowerCase();
    if (!r) return;
    setter((prev) => {
      const set = new Set((prev || []).map((x) => String(x).toLowerCase().trim()).filter(Boolean));
      if (set.has(r)) set.delete(r);
      else set.add(r);
      return Array.from(set);
    });
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="User Management"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy && !creating) onClose?.();
      }}
      style={overlayStyle}
    >
      <div style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ fontSize: 16, fontWeight: 900 }}>👤 User Management</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Auth roles = admin/staff. App roles = RBAC roles (dynamic).
            </div>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button className="btn" onClick={() => !busy && !creating && refreshUsers()} disabled={busy || creating}>
              ⟳ Refresh
            </button>
            <button className="btn" onClick={() => !busy && !creating && onClose?.()} disabled={busy || creating}>
              ✖ Close
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={tabsStyle}>
          <TabButton active={tab === "users"} onClick={() => setTab("users")} disabled={busy || creating}>
            Users
          </TabButton>
          <TabButton active={tab === "create"} onClick={() => setTab("create")} disabled={busy || creating}>
            ➕ Create User
          </TabButton>

          <div style={{ marginLeft: "auto", fontSize: 12, opacity: 0.75 }}>
            {busy ? "Working…" : `${filteredUsers.length} users`}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 16 }}>
          {tab === "users" && (
            <>
              {/* Filters */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div style={{ gridColumn: "span 6" }}>
                  <div style={labelStyle}>Search</div>
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="email / name / auth role / rbac role / branch"
                    style={fieldStyle}
                  />
                </div>

                <div style={{ gridColumn: "span 2" }}>
                  <div style={labelStyle}>Status</div>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={fieldStyle}>
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div style={{ gridColumn: "span 2" }}>
                  <div style={labelStyle}>RBAC Role</div>
                  <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={fieldStyle}>
                    <option value="">All</option>
                    {RBAC_ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: "span 2" }}>
                  <div style={labelStyle}>Branch</div>
                  <select value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)} style={fieldStyle}>
                    <option value="">All</option>
                    {BRANCH_OPTIONS.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {loadErr && <div style={errorBoxStyle}>❌ {loadErr}</div>}

              {/* List */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {filteredUsers.map((u) => (
                  <div key={u.email} style={userCardStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontWeight: 900, fontSize: 14 }}>
                        {u.first_name || ""} {u.last_name || ""}
                      </div>
                      <div style={{ opacity: 0.85, fontSize: 12 }}>
                        <code style={codeStyle}>{u.email}</code>
                      </div>

                      <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                        <StatusPill active={u.active !== false} />
                        <button className="btn" disabled={busy || creating} onClick={() => openEdit(u)}>
                          ✏ Edit
                        </button>
                        <button className="btn" disabled={busy || creating} onClick={() => toggleActive(u)}>
                          {u.active !== false ? "⛔ Deactivate" : "✅ Activate"}
                        </button>
                        <button className="btn" disabled={busy || creating} onClick={() => deleteUser(u)}>
                          🗑 Delete
                        </button>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                      <MiniTag label="Auth Roles" value={(u.roles || []).join(", ") || "-"} />
                      <MiniTag label="RBAC Roles" value={(u.rbac_roles || []).join(", ") || "-"} />
                      <MiniTag label="Branches" value={(u.branches || []).join(", ") || "-"} />
                    </div>
                  </div>
                ))}

                {!busy && !filteredUsers.length && (
                  <div style={{ opacity: 0.8, fontSize: 13, padding: 14 }}>No users found.</div>
                )}
              </div>

              {/* Edit panel */}
              {editing && editDraft && (
                <div style={editPanelOverlay}>
                  <div style={editPanel}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ fontWeight: 900 }}>Edit User</div>
                      <div style={{ marginLeft: "auto", opacity: 0.85, fontSize: 12 }}>
                        <code style={codeStyle}>{editDraft.email}</code>
                      </div>
                      <button className="btn" onClick={closeEdit} disabled={busy}>
                        ✖
                      </button>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 10, marginTop: 12 }}>
                      <div style={{ gridColumn: "span 6" }}>
                        <div style={labelStyle}>First name</div>
                        <input
                          value={editDraft.first_name}
                          onChange={(e) => setEditDraft((p) => ({ ...p, first_name: e.target.value }))}
                          style={fieldStyle}
                        />
                      </div>

                      <div style={{ gridColumn: "span 6" }}>
                        <div style={labelStyle}>Last name</div>
                        <input
                          value={editDraft.last_name}
                          onChange={(e) => setEditDraft((p) => ({ ...p, last_name: e.target.value }))}
                          style={fieldStyle}
                        />
                      </div>

                      {/* AUTH roles */}
                      <div style={{ gridColumn: "span 12" }}>
                        <div style={labelStyle}>Auth Roles (admin/staff)</div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          {AUTH_ROLE_OPTIONS.map((r) => {
                            const on = (editDraft.auth_roles || []).map(String).includes(r.value);
                            return (
                              <button
                                key={r.value}
                                type="button"
                                className="btn"
                                disabled={busy}
                                onClick={() =>
                                  setEditDraft((p) => {
                                    const set = new Set(p.auth_roles || []);
                                    if (set.has(r.value)) set.delete(r.value);
                                    else set.add(r.value);
                                    return { ...p, auth_roles: Array.from(set) };
                                  })
                                }
                                style={{
                                  ...pillBtnStyle,
                                  background: on ? "rgba(99,102,241,.20)" : "rgba(255,255,255,.06)",
                                  borderColor: on ? "rgba(99,102,241,.45)" : "rgba(255,255,255,.12)",
                                }}
                              >
                                {on ? "✅ " : ""}{r.label}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                          Sent to auth as: <code style={codeStyle}>{rolesToStr(editDraft.auth_roles)}</code>
                        </div>
                      </div>

                      {/* RBAC roles */}
                      <div style={{ gridColumn: "span 12" }}>
                        <div style={labelStyle}>RBAC Roles (dynamic)</div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          {RBAC_ROLE_OPTIONS.map((r) => {
                            const on = (editDraft.rbac_roles || []).includes(r.value);
                            return (
                              <button
                                key={r.value}
                                type="button"
                                className="btn"
                                disabled={busy}
                                onClick={() => toggleRbacRole(r.value, (fn) => setEditDraft((p) => ({ ...p, rbac_roles: fn(p.rbac_roles) })))}
                                style={{
                                  ...pillBtnStyle,
                                  background: on ? "rgba(34,197,94,.18)" : "rgba(255,255,255,.06)",
                                  borderColor: on ? "rgba(34,197,94,.45)" : "rgba(255,255,255,.12)",
                                }}
                                title={r.description || r.value}
                              >
                                {on ? "✅ " : ""}{r.label}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                          Saved to RBAC as: <code style={codeStyle}>{JSON.stringify(editDraft.rbac_roles || [])}</code>
                        </div>
                      </div>

                      {/* Branches */}
                      <div style={{ gridColumn: "span 12" }}>
                        <div style={labelStyle}>Branches</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, userSelect: "none" }}>
                            <input
                              type="checkbox"
                              checked={(editDraft.branches || []).includes("*")}
                              disabled={busy}
                              onChange={(e) => {
                                const on = e.target.checked;
                                if (on) setEditDraft((p) => ({ ...p, branches: ["*"] }));
                                else setEditDraft((p) => ({ ...p, branches: [] }));
                              }}
                            />
                            <span style={{ fontWeight: 800 }}>All branches (*)</span>
                          </label>

                          <label style={{ display: "flex", alignItems: "center", gap: 8, userSelect: "none" }}>
                            <input
                              type="checkbox"
                              checked={!!editDraft.active}
                              disabled={busy}
                              onChange={(e) => setEditDraft((p) => ({ ...p, active: e.target.checked }))}
                            />
                            <span style={{ fontWeight: 800 }}>Active</span>
                          </label>
                        </div>

                        {!(editDraft.branches || []).includes("*") && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                              gap: 10,
                              padding: 12,
                              borderRadius: 14,
                              border: "1px solid rgba(255,255,255,.12)",
                              background: "rgba(255,255,255,.03)",
                            }}
                          >
                            {BRANCH_OPTIONS.map((b) => {
                              const checked = (editDraft.branches || []).includes(b.value);
                              return (
                                <button
                                  key={b.value}
                                  type="button"
                                  disabled={busy}
                                  onClick={() =>
                                    setEditDraft((p) => {
                                      const next = Array.isArray(p.branches) ? p.branches : [];
                                      const set = new Set(next);
                                      if (set.has(b.value)) set.delete(b.value);
                                      else set.add(b.value);
                                      return { ...p, branches: Array.from(set) };
                                    })
                                  }
                                  style={{
                                    ...pillStyle,
                                    background: checked ? "rgba(34,197,94,.18)" : "rgba(255,255,255,.06)",
                                    borderColor: checked ? "rgba(34,197,94,.45)" : "rgba(255,255,255,.12)",
                                    opacity: busy ? 0.6 : 1,
                                  }}
                                >
                                  <span style={{ fontSize: 18 }}>{checked ? "✅" : "⬜"}</span>
                                  <span style={{ fontWeight: 800 }}>{b.label}</span>
                                  <span style={{ marginLeft: "auto", opacity: 0.7, fontSize: 12 }}>{b.value}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 10 }}>
                          Selected: <code style={codeStyle}>{JSON.stringify(editDraft.branches || [])}</code>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
                      <button className="btn" onClick={closeEdit} disabled={busy}>
                        Cancel
                      </button>
                      <button className="btn primary" onClick={saveEdit} disabled={busy} style={{ minWidth: 150 }}>
                        {busy ? "Saving…" : "Save Changes"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "create" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 12 }}>
              <div style={{ gridColumn: "span 12" }}>
                <div style={labelStyle}>Email</div>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="user@gmail.com"
                  autoFocus
                  style={fieldStyle}
                />
              </div>

              <div style={{ gridColumn: "span 12" }}>
                <div style={labelStyle}>Password</div>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Set a password"
                  style={fieldStyle}
                />
              </div>

              <div style={{ gridColumn: "span 6" }}>
                <div style={labelStyle}>First Name</div>
                <input value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} placeholder="First name" style={fieldStyle} />
              </div>

              <div style={{ gridColumn: "span 6" }}>
                <div style={labelStyle}>Last Name</div>
                <input value={newLastName} onChange={(e) => setNewLastName(e.target.value)} placeholder="Family name" style={fieldStyle} />
              </div>

              {/* AUTH roles */}
              <div style={{ gridColumn: "span 12" }}>
                <div style={labelStyle}>Auth Roles (admin/staff)</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <PillRadio label="Staff" active={newAuthRoles === "staff"} onClick={() => setNewAuthRoles("staff")} disabled={creating} />
                  <PillRadio label="Admin" active={newAuthRoles === "admin"} onClick={() => setNewAuthRoles("admin")} disabled={creating} />
                  <PillRadio
                    label="Admin + Staff"
                    active={newAuthRoles === "admin,staff" || newAuthRoles === "staff,admin"}
                    onClick={() => setNewAuthRoles("admin,staff")}
                    disabled={creating}
                  />
                </div>
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
                  Sent to auth as: <code style={codeStyle}>{newAuthRoles}</code>
                </div>
              </div>

              {/* RBAC roles */}
              <div style={{ gridColumn: "span 12" }}>
                <div style={labelStyle}>RBAC Roles (dynamic)</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {RBAC_ROLE_OPTIONS.map((r) => {
                    const on = (newRbacRoles || []).includes(r.value);
                    return (
                      <button
                        key={r.value}
                        type="button"
                        className="btn"
                        disabled={creating}
                        onClick={() => toggleRbacRole(r.value, setNewRbacRoles)}
                        style={{
                          ...pillBtnStyle,
                          background: on ? "rgba(34,197,94,.18)" : "rgba(255,255,255,.06)",
                          borderColor: on ? "rgba(34,197,94,.45)" : "rgba(255,255,255,.12)",
                        }}
                        title={r.description || r.value}
                      >
                        {on ? "✅ " : ""}{r.label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
                  Saved to RBAC as: <code style={codeStyle}>{JSON.stringify(newRbacRoles || [])}</code>
                </div>
              </div>

              {/* Branches */}
              <div style={{ gridColumn: "span 12" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div>
                    <div style={labelStyle}>Branches</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      Choose one or more branches. Or select <b>All branches</b>.
                    </div>
                  </div>

                  <label style={{ display: "flex", alignItems: "center", gap: 8, userSelect: "none" }}>
                    <input
                      type="checkbox"
                      checked={newAllBranches}
                      disabled={creating}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setNewAllBranches(on);
                        if (on) setNewBranchesSelected([]);
                      }}
                    />
                    <span style={{ fontWeight: 800 }}>All branches (*)</span>
                  </label>
                </div>

                {!newAllBranches && (
                  <div
                    style={{
                      marginTop: 12,
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                      gap: 10,
                      padding: 12,
                      borderRadius: 14,
                      border: "1px solid rgba(255,255,255,.12)",
                      background: "rgba(255,255,255,.03)",
                    }}
                  >
                    {BRANCH_OPTIONS.map((b) => {
                      const checked = newBranchesSelected.includes(b.value);
                      return (
                        <button
                          key={b.value}
                          type="button"
                          onClick={() => !creating && toggleBranch(b.value, setNewBranchesSelected)}
                          style={{
                            ...pillStyle,
                            background: checked ? "rgba(34,197,94,.18)" : "rgba(255,255,255,.06)",
                            borderColor: checked ? "rgba(34,197,94,.45)" : "rgba(255,255,255,.12)",
                            opacity: creating ? 0.6 : 1,
                          }}
                        >
                          <span style={{ fontSize: 18 }}>{checked ? "✅" : "⬜"}</span>
                          <span style={{ fontWeight: 800 }}>{b.label}</span>
                          <span style={{ marginLeft: "auto", opacity: 0.7, fontSize: 12 }}>{b.value}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 10 }}>
                  Selected: <code style={codeStyle}>{newAllBranches ? "['*']" : JSON.stringify(newBranchesSelected)}</code>
                </div>
              </div>

              <div style={{ gridColumn: "span 12" }}>
                <div style={noteStyle}>
                  <b>Security:</b> Requires admin + CSRF cookie. If you get <code style={codeStyle}>403 CSRF</code>, refresh and login again.
                </div>
              </div>

              <div style={{ gridColumn: "span 12", display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button className="btn" disabled={creating} onClick={resetNewUserForm}>
                  ♻ Reset
                </button>
                <button className="btn primary" disabled={creating} onClick={handleCreateUser} style={{ minWidth: 160 }}>
                  {creating ? "Creating…" : "Create User"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <div style={{ fontSize: 12, opacity: 0.75 }}>Tip: deactivate users instead of deleting when possible.</div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            <button className="btn" disabled={busy || creating} onClick={() => onClose?.()}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- UI bits -------------------- */

function TabButton({ active, children, onClick, disabled }) {
  return (
    <button
      className="btn"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "10px 12px",
        borderRadius: 999,
        fontWeight: 900,
        border: "1px solid rgba(255,255,255,.12)",
        background: active ? "rgba(99,102,241,.22)" : "rgba(255,255,255,.06)",
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {children}
    </button>
  );
}

function StatusPill({ active }) {
  return (
    <span
      style={{
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 900,
        border: "1px solid rgba(255,255,255,.14)",
        background: active ? "rgba(34,197,94,.18)" : "rgba(239,68,68,.18)",
      }}
      title={active ? "Active" : "Inactive"}
    >
      {active ? "✅ Active" : "⛔ Inactive"}
    </span>
  );
}

function MiniTag({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid rgba(255,255,255,.12)",
        background: "rgba(255,255,255,.04)",
        fontSize: 12,
      }}
    >
      <span style={{ opacity: 0.7 }}>{label}:</span>
      <span style={{ fontWeight: 800 }}>{value}</span>
    </div>
  );
}

function PillRadio({ label, active, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "10px 12px",
        borderRadius: 999,
        border: "1px solid rgba(255,255,255,.12)",
        background: active ? "rgba(99,102,241,.20)" : "rgba(255,255,255,.06)",
        color: "inherit",
        fontWeight: 900,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1,
      }}
      className="btn"
      title={label}
    >
      {active ? "✅ " : ""}{label}
    </button>
  );
}

/* -------------------- styles -------------------- */

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  background: "rgba(2, 6, 23, 0.75)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "center",
  padding:
    "max(14px, env(safe-area-inset-top)) max(14px, env(safe-area-inset-right)) max(14px, env(safe-area-inset-bottom)) max(14px, env(safe-area-inset-left))",
};

const modalStyle = {
  width: "min(1100px, 100%)",
  height: "100%",
  borderRadius: 18,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(15, 23, 42, .96)",
  boxShadow: "0 20px 80px rgba(0,0,0,.55)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const headerStyle = {
  padding: "14px 14px",
  borderBottom: "1px solid rgba(255,255,255,.10)",
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const tabsStyle = {
  padding: "10px 14px",
  borderBottom: "1px solid rgba(255,255,255,.10)",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const footerStyle = {
  padding: 14,
  borderTop: "1px solid rgba(255,255,255,.10)",
  display: "flex",
  gap: 10,
  alignItems: "center",
};

const labelStyle = { fontSize: 12, opacity: 0.85, marginBottom: 6 };

const fieldStyle = {
  width: "100%",
  padding: "12px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(255,255,255,.04)",
  color: "inherit",
  outline: "none",
};

const codeStyle = {
  padding: "2px 6px",
  borderRadius: 8,
  background: "rgba(255,255,255,.06)",
  border: "1px solid rgba(255,255,255,.10)",
};

const pillStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 12px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,.12)",
  cursor: "pointer",
  color: "inherit",
};

const pillBtnStyle = {
  padding: "10px 12px",
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,.12)",
  fontWeight: 900,
};

const noteStyle = {
  padding: 12,
  borderRadius: 14,
  border: "1px dashed rgba(255,255,255,.18)",
  background: "rgba(255,255,255,.03)",
  fontSize: 12,
  opacity: 0.85,
  lineHeight: 1.5,
};

const errorBoxStyle = {
  padding: 12,
  borderRadius: 14,
  border: "1px solid rgba(239,68,68,.35)",
  background: "rgba(239,68,68,.12)",
  marginBottom: 12,
  fontSize: 13,
};

const userCardStyle = {
  padding: 12,
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(255,255,255,.03)",
};

const editPanelOverlay = {
  position: "fixed",
  inset: 0,
  zIndex: 10001,
  background: "rgba(0,0,0,.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 14,
};

const editPanel = {
  width: "min(860px, 100%)",
  borderRadius: 18,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(15, 23, 42, .98)",
  boxShadow: "0 20px 80px rgba(0,0,0,.65)",
  padding: 14,
};